import React from "react";
import { useI18n } from "@/i18n";
import { useReveal } from "@/hooks/useReveal";

export default function Hero() {
  const { t } = useI18n();
  const a = useReveal();
  const b = useReveal();
  const c = useReveal();
  const d = useReveal();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-20 md:pb-32 overflow-hidden"
    >
      {/* Background gradient accent */}
      <div
        aria-hidden
        className="absolute inset-x-0 -top-40 h-[520px] pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 60% at 50% 30%, rgba(201,168,106,0.10) 0%, rgba(255,255,255,0) 70%)",
        }}
      />
      <div className="max-w-[1240px] mx-auto px-5 md:px-8 relative">
        {/* Eyebrow */}
        <div
          ref={a.ref}
          className={`reveal ${a.visible ? "is-visible" : ""} flex justify-center`}
        >
          <span
            data-testid="hero-eyebrow"
            className="wf-chip inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[11px] tracking-[0.18em] uppercase text-neutral-700 whitespace-nowrap"
          >
            <span
              aria-hidden
              className="w-1 h-1 rounded-full"
              style={{ background: "#c9a86a" }}
            />
            {t.hero.eyebrow}
          </span>
        </div>

        {/* Headline */}
        <h1
          ref={b.ref}
          data-testid="hero-headline"
          className={`reveal reveal-delay-1 ${
            b.visible ? "is-visible" : ""
          } font-display text-center mx-auto mt-7 md:mt-9 max-w-[14ch] text-[40px] leading-[1.02] sm:text-[56px] md:text-[76px] lg:text-[92px] font-light tracking-[-0.03em] text-neutral-950`}
        >
          <span className="block">{t.hero.headline_1}</span>
          <span className="block font-serif-accent italic text-neutral-900">
            {t.hero.headline_2}
          </span>
          <span className="block">{t.hero.headline_3}</span>
          <span
            className="block"
            style={{
              background:
                "linear-gradient(90deg,#c9a86a 0%, #0a0a0a 50%, #c9a86a 100%)",
              backgroundSize: "200% auto",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            {t.hero.headline_4}
          </span>
        </h1>

        {/* Sub */}
        <p
          ref={c.ref}
          data-testid="hero-sub"
          className={`reveal reveal-delay-2 ${
            c.visible ? "is-visible" : ""
          } mt-8 md:mt-10 mx-auto text-center max-w-[620px] text-[15px] md:text-[17px] leading-relaxed text-neutral-600`}
        >
          {t.hero.sub}
        </p>

        {/* CTAs */}
        <div
          ref={d.ref}
          className={`reveal reveal-delay-3 ${
            d.visible ? "is-visible" : ""
          } mt-9 md:mt-11 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4`}
        >
          <a
            href="/login"
            data-testid="hero-cta-primary"
            className="wf-btn-primary rounded-full px-7 py-4 text-[14px] tracking-wide w-full sm:w-auto text-center"
          >
            {t.hero.cta_primary}
          </a>
          <a
            href="#how"
            data-testid="hero-cta-secondary"
            className="wf-btn-ghost rounded-full px-6 py-4 text-[14px] tracking-wide w-full sm:w-auto text-center border border-neutral-200"
          >
            {t.hero.cta_secondary} →
          </a>
        </div>

        <p
          data-testid="hero-meta"
          className="mt-6 text-center text-[12px] tracking-[0.18em] uppercase text-neutral-500"
        >
          {t.hero.meta}
        </p>

        {/* Hero image - editorial wide frame */}
        <div className="mt-14 md:mt-20 relative">
          <div
            data-testid="hero-image-wrap"
            className="relative overflow-hidden rounded-[14px] border border-neutral-200 shadow-[0_40px_100px_-40px_rgba(10,10,10,0.25)] aspect-[16/10] md:aspect-[16/8]"
          >
            <img
              src="https://images.pexels.com/photos/12438966/pexels-photo-12438966.jpeg"
              alt="Sposi abbracciati in un momento intimo"
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
              fetchPriority="high"
            />
            <div
              aria-hidden
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.0) 60%, rgba(255,255,255,0.35) 100%)",
              }}
            />
            {/* Floating glass card */}
            <div
              data-testid="hero-floating-card"
              className="hidden md:flex absolute left-6 bottom-6 items-center gap-3 px-4 py-3 rounded-2xl"
              style={{
                background: "rgba(255,255,255,0.72)",
                backdropFilter: "blur(18px)",
                WebkitBackdropFilter: "blur(18px)",
                border: "1px solid rgba(255,255,255,0.6)",
                boxShadow: "0 20px 60px -20px rgba(10,10,10,0.25)",
              }}
            >
              <div
                className="w-9 h-9 rounded-xl grid place-items-center"
                style={{
                  background: "linear-gradient(135deg,#0a0a0a,#2a2a2a)",
                  color: "#fff",
                }}
              >
                <span className="text-[11px] tracking-widest">QR</span>
              </div>
              <div className="leading-tight">
                <div className="text-[11px] tracking-[0.18em] uppercase text-neutral-500">
                  Live
                </div>
                <div className="text-[13px] text-neutral-900 font-medium">
                  342 ricordi caricati
                </div>
              </div>
            </div>
          </div>

          {/* Scroll cue */}
          <div className="hidden md:flex flex-col items-center gap-2 mt-10 text-neutral-500">
            <span className="text-[11px] tracking-[0.24em] uppercase">
              Scopri
            </span>
            <span className="wf-scroll-cue">↓</span>
          </div>
        </div>
      </div>
    </section>
  );
}
